/**
 * Staffing Plan — P6 extracts, Excel `1S LGL-Staff-Plan` style.
 *
 * Tables:
 *   Baseline — Hrs                      P6 baseline schedule resource assignment hours
 *   Baseline — manpower                 Baseline Hrs ÷ P6 calendar(s)
 *   Earned + Remaining — Hrs            Current schedule: Actual Units to the data date, then Remaining
 *   Earned + Remaining — manpower       Hrs ÷ P6 calendar(s); forecast follows Hiring/layoff overrides
 *   Hiring / layoff                     week-over-week manpower change (future cells editable)
 *   Difference                          (Earned + Remaining) − Baseline, in men
 *
 * Curves: Actual blue · Remaining green — they share the data-date week so the
 * line hands off. Baseline gray→black at the data date · data date yellow.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  LabelList,
  Cell
} from 'recharts';
import {
  buildStaffingModel,
  familyFromResource,
  normalizeSafetyRule,
  safetyThresholdsUpTo,
  safetyLineLabelCount,
  safetyCountWord,
  safetyRuleLabel,
  buildSupervisionPlan,
  supervisionThresholds,
  supervisionRatiosForResources,
  neededAt,
  gfNeededAt
} from '../../utils/staffingPlan';
import { useMsal } from '@azure/msal-react';
import { exportFilename } from '../../utils/nomenclature';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { addExtrasWorksheet, downloadBlob, styleHeaderRow } from '../../utils/planExport';
import { extrasTableRows, loadExtrasCfg, projectMonthKeys } from '../../utils/gcIndirectExtras';
import { resourceColor, resourceFilterLabel, stackMeta, legendColor, legendPattern } from '../../utils/evmLegend';
import LegendColorSwatch from './LegendColorSwatch';
import { hatchedBarShape } from './HatchedBarShape';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const OPEN_KEY = 'evm-staffing-open-v3';
const ADJUST_KEY = 'evm-staffing-adjust-v1';
const COL_W = 46;
const LABEL_W = 168;
const MAIN_H = 340;
const HIRE_H = 100;
const M_TOP = 8;
const M_BOT = 8;
const TS_YEAR_H = 22;
const TS_MONTH_H = 20;
const TS_DAY_H = 24;

const CURVE_COLORS = {
  actual: '#2563eb',    // Actual (earned) — blue
  remaining: '#16a34a', // Remaining / forecast — green
  basePast: '#9ca3af',  // Baseline up to the data date — gray
  baseFuture: '#111827', // Baseline after the data date — black
  dataDate: '#eab308'   // Data date — yellow
};

const EMPTY_SUPER_CFG = { from: '', to: '', overrides: {} };

function defaultOpen() {
  return { baselineHours: false, baselineMen: true, curHours: false, curMen: true, hire: true, diff: true, progress: true };
}

function loadOpen() {
  try {
    const raw = JSON.parse(localStorage.getItem(OPEN_KEY) || 'null');
    if (raw && typeof raw === 'object') return { ...defaultOpen(), ...raw };
  } catch { /* ignore */ }
  return defaultOpen();
}

function saveOpen(next) {
  try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
}

function loadAdjust(jobNumber) {
  try {
    const raw = JSON.parse(localStorage.getItem(`${ADJUST_KEY}-${jobNumber}`) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch { return {}; }
}

function saveAdjust(jobNumber, map) {
  try { localStorage.setItem(`${ADJUST_KEY}-${jobNumber}`, JSON.stringify(map)); } catch { /* ignore */ }
}

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Last week-start on or before ymd (weeks are sorted Sunday keys). */
function weekContaining(weeks, ymd) {
  const t = String(ymd || '').slice(0, 10);
  if (!t || !weeks?.length) return null;
  let hit = null;
  for (const w of weeks) {
    if (w <= t) hit = w;
    else break;
  }
  return hit || weeks[0];
}

function formatWeek(ymd) {
  if (!ymd) return { day: '', mon: '', year: '', tip: '' };
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  return {
    day: String(d),
    mon: MONTHS[(m || 1) - 1] || '',
    year: String(y),
    tip: `${d} ${MONTHS[(m || 1) - 1]} ${y}`
  };
}

function signed(n) {
  if (!n) return '0';
  return n > 0 ? `+${n}` : String(n);
}

function csvEscape(v) {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, rows) {
  const text = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Shared nice scale so the frozen HTML axis matches the chart grid exactly. */
function niceStep(rough) {
  const pow = 10 ** Math.floor(Math.log10(Math.max(rough, 1e-6)));
  for (const s of [1, 2, 2.5, 5, 10]) {
    if (s * pow >= rough) return s * pow;
  }
  return 10 * pow;
}

function scaleFor(maxValue, parts = 4) {
  const m = Math.max(1, maxValue || 0);
  const step = niceStep(m / parts);
  const top = Math.ceil(m / step - 1e-9) * step;
  const ticks = [];
  for (let v = 0; v <= top + 1e-9; v += step) ticks.push(Math.round(v * 100) / 100);
  return { max: top, ticks };
}

function timescaleGroups(weeks) {
  const years = [];
  const months = [];
  weeks.forEach((w) => {
    const y = w.slice(0, 4);
    const m = w.slice(0, 7);
    if (years.length && years[years.length - 1].id === y) years[years.length - 1].span += 1;
    else years.push({ id: y, label: y, span: 1 });
    if (months.length && months[months.length - 1].id === m) months[months.length - 1].span += 1;
    else months.push({ id: m, label: MONTHS[Number(w.slice(5, 7)) - 1] || '', span: 1 });
  });
  return { years, months };
}

const SECTIONS = [
  { id: 'baselineHours', title: 'Baseline — Hrs', hint: 'P6 baseline schedule resource assignment hours (baseline picked above)', field: 'baselineHours', kind: 'hours' },
  { id: 'baselineMen', title: 'Baseline — manpower', hint: 'Baseline Hrs ÷ P6 calendar(s), rounded to whole people', field: 'baselineMen', kind: 'men' },
  { id: 'curHours', title: 'Earned + Remaining — Hrs (Current)', hint: 'Current schedule: Actual Units to the data date, then Remaining Units under the FAC formula', field: 'curHours', kind: 'hours' },
  { id: 'curMen', title: 'Earned + Remaining — manpower', hint: 'Hrs ÷ P6 calendar(s). The forecast side follows your Hiring / layoff overrides', field: 'curMen', kind: 'men', adjusted: true },
  { id: 'hire', title: 'Hiring / layoff', hint: 'This week minus last week of the manpower row — double-click a future cell to override, empty resets', field: 'hire', kind: 'delta', editable: true },
  { id: 'diff', title: 'Difference — (Earned + Remaining) − Baseline', hint: 'Plus = running or planning heavier than the baseline that week', field: 'diff', kind: 'delta', adjusted: true }
];

function StaffingPlanTab({
  remaining,
  actuals,
  baseline,
  baselineLabel = '',
  forecastFactor = 1,
  forecastLabel = '',
  dataDate,
  hpwForResource,
  weekHoursDefault = 40,
  selectedDisciplines = [],
  allDisciplines = [],
  selectedFamilies = [],
  allFamilies = [],
  selectedTypes = ['shop', 'field'],
  progressScopes = [],
  selectedResourceIds = null,
  legendBy = 'resource',
  dateFrom = '',
  dateTo = '',
  jobNumber,
  loading,
  dataSource,
  progressTable = null,
  safetyOn = false,
  safetyRule: safetyRuleProp,
  onSafetyCrew,
  supervisionOn = false,
  menPerFm = 8,
  fmPerGf = 3,
  onMenPerFm,
  onFmPerGf,
  superCfg: superCfgProp,
  onPatchCfg,
  showBarCounts = false,
  legendColors = {},
  onLegendColor,
  onLegendPattern,
  radius = 0,
  horizon = 'future',
  view = 'curves',
  csvRef,
  resetRef,
  onSummaryChange
}) {
  const { instance, accounts } = useMsal();
  const [exporting, setExporting] = useState(null);
  const [openSections, setOpenSections] = useState(loadOpen);
  const [hoverWeek, setHoverWeek] = useState(null);
  const [spotlight, setSpotlight] = useState(null);
  const hoverWeekRef = React.useRef(null);
  const [adjust, setAdjust] = useState(() => loadAdjust(jobNumber));
  const superCfg = superCfgProp || EMPTY_SUPER_CFG;
  const [superStripOpen, setSuperStripOpen] = useState(true);

  useEffect(() => {
    setAdjust(loadAdjust(jobNumber));
  }, [jobNumber]);

  const shopField = selectedTypes.length === 1 ? selectedTypes[0] : null;
  const scope = progressScopes?.length === 1 ? progressScopes[0] : null;

  const model = useMemo(() => buildStaffingModel({
    baseline,
    remaining,
    actuals,
    dataDate,
    radius,
    forecastFactor,
    weekHoursFn: (d, meta) => (
      typeof hpwForResource === 'function'
        ? hpwForResource(d, meta)
        : (Number(meta?.weekHrCnt) > 0 ? Number(meta.weekHrCnt) : weekHoursDefault)
    ),
    filters: {
      disciplines: selectedDisciplines,
      allDisciplines,
      families: selectedFamilies,
      allFamilies,
      shopField,
      scope: scope === 'all' ? null : scope,
      resourceIds: Array.isArray(selectedResourceIds) ? selectedResourceIds : undefined
    }
  }), [baseline, remaining, actuals, dataDate, radius, forecastFactor, hpwForResource, weekHoursDefault,
    selectedDisciplines, allDisciplines, selectedFamilies, allFamilies, shopField, scope, selectedResourceIds]);

  const cursorWeek = useMemo(
    () => weekContaining(model.weeks, dataDate || todayYmd()),
    [model.weeks, dataDate]
  );

  const weeks = useMemo(() => {
    return (model.weeks || []).filter((w) => {
      if (dateFrom && w < dateFrom) return false;
      if (dateTo && w > dateTo) return false;
      if (horizon === 'future' && cursorWeek && w < cursorWeek) return false;
      if (horizon === 'past' && cursorWeek && w >= cursorWeek) return false;
      return true;
    });
  }, [model.weeks, dateFrom, dateTo, horizon, cursorWeek]);

  const weekIndex = useMemo(() => new Map((model.weeks || []).map((w, i) => [w, i])), [model.weeks]);

  /** Forecast manpower rebuilt from hiring/layoff (override ?? default), Excel style. */
  const adjusted = useMemo(() => {
    const menBy = new Map();
    const hireBy = new Map();
    (model.resources || []).forEach((r) => {
      const men = r.curMen.slice();
      const hire = r.hire.slice();
      for (let i = Math.max(model.cutIndex, 0); i < men.length; i++) {
        const o = adjust[`${r.resourceId}|${model.weeks[i]}`];
        const defH = (r.curMen[i] || 0) - (i > 0 ? (r.curMen[i - 1] || 0) : 0);
        const h = o == null ? defH : (Number(o) || 0);
        const prev = i > 0 ? men[i - 1] : 0;
        hire[i] = h;
        men[i] = prev + h;
      }
      menBy.set(r.resourceId, men);
      hireBy.set(r.resourceId, hire);
    });
    return { menBy, hireBy };
  }, [model, adjust]);

  const visibleResources = useMemo(() => {
    return (model.resources || []).filter((r) => weeks.some((w) => {
      const i = weekIndex.get(w);
      return Math.abs(r.baselineHours[i] || 0)
        + Math.abs(r.curHours[i] || 0)
        + Math.abs((adjusted.menBy.get(r.resourceId) || [])[i] || 0) > 0;
    }));
  }, [model.resources, weeks, weekIndex, adjusted]);

  const legendSeries = useMemo(() => {
    const map = new Map();
    visibleResources.forEach((r) => {
      const meta = stackMeta(r, legendBy);
      if (!map.has(meta.id)) map.set(meta.id, { ...meta, resources: [] });
      map.get(meta.id).resources.push(r);
    });
    return [...map.values()].map((item) => ({
      ...item,
      color: legendColor(legendColors, item.id, item.color),
      pattern: legendPattern(legendColors, item.id)
    }));
  }, [visibleResources, legendBy, legendColors]);

  const adjustCount = useMemo(
    () => Object.keys(adjust).filter((k) => adjust[k] != null).length,
    [adjust]
  );

  const setHireAdjust = (resourceId, week, value) => {
    setAdjust((prev) => {
      const next = { ...prev };
      const key = `${resourceId}|${week}`;
      if (value == null || value === '') delete next[key];
      else next[key] = Number(value) || 0;
      saveAdjust(jobNumber, next);
      return next;
    });
  };

  const resetAdjust = () => {
    setAdjust({});
    saveAdjust(jobNumber, {});
  };

  const chartData = useMemo(() => weeks.map((date) => {
    const i = weekIndex.get(date);
    const row = { date, baseline: 0, cur: 0 };
    visibleResources.forEach((r) => {
      row.baseline += r.baselineMen[i] || 0;
      row.cur += (adjusted.menBy.get(r.resourceId) || [])[i] || 0;
    });
    legendSeries.forEach((item) => {
      row[item.id] = item.resources.reduce(
        (s, r) => s + ((adjusted.menBy.get(r.resourceId) || [])[i] || 0), 0
      );
    });
    // Share the data-date week so blue hands off to green (same Y).
    row.actual = i <= model.cutIndex ? row.cur : null;
    row.remaining = i >= model.cutIndex ? row.cur : null;
    row.basePast = i <= model.cutIndex ? row.baseline : null;
    row.baseFuture = i >= model.cutIndex ? row.baseline : null;
    row.hire = visibleResources.reduce(
      (s, r) => s + ((adjusted.hireBy.get(r.resourceId) || [])[i] || 0), 0
    );
    return row;
  }), [weeks, weekIndex, visibleResources, legendSeries, model.cutIndex, adjusted]);

  const mainScale = useMemo(() => {
    let m = 0;
    chartData.forEach((row) => { m = Math.max(m, row.cur || 0, row.baseline || 0); });
    return scaleFor(m);
  }, [chartData]);

  const safetyRule = useMemo(() => normalizeSafetyRule(safetyRuleProp), [safetyRuleProp]);

  /** Threshold lines on the crew chart — every-N, first-then, or three-step. */
  const crewPeak = useMemo(() => {
    let peak = 0;
    chartData.forEach((row) => { peak = Math.max(peak, row.cur || 0, row.baseline || 0); });
    return peak;
  }, [chartData]);

  const safetyLevels = useMemo(() => {
    if (!safetyOn) return [];
    return safetyThresholdsUpTo(crewPeak, safetyRule);
  }, [safetyOn, safetyRule, crewPeak]);

  const supervisionLevels = useMemo(() => {
    if (!supervisionOn) return { fm: [], gf: [], perFm: menPerFm, gfMen: menPerFm * fmPerGf };
    const ratios = supervisionRatiosForResources(visibleResources, menPerFm, fmPerGf, superCfg.overrides || {});
    return supervisionThresholds(crewPeak, ratios.menPerFm, ratios.fmPerGf);
  }, [supervisionOn, crewPeak, visibleResources, menPerFm, fmPerGf, superCfg]);
  const staffGfSet = new Set(supervisionLevels.gf);

  /** Crew over the FULL schedule (all model weeks, hiring overrides applied) for the safety plan. */
  const safetyCrew = useMemo(() => (model.weeks || []).map((w, i) => {
    let men = 0;
    let indirect = 0;
    (model.resources || []).forEach((r) => {
      const v = (adjusted.menBy.get(r.resourceId) || [])[i] || 0;
      men += v;
      if (familyFromResource(r) === 'indirect') indirect += v;
    });
    return { date: w, men, indirect };
  }), [model, adjusted]);

  useEffect(() => {
    if (typeof onSafetyCrew !== 'function') return;
    onSafetyCrew(safetyCrew);
  }, [safetyCrew, onSafetyCrew]);

  const superPlan = useMemo(() => buildSupervisionPlan({
    weeks: model.weeks || [],
    resources: model.resources || [],
    menByResource: adjusted.menBy,
    from: superCfg.from || '',
    to: superCfg.to || '',
    menPerFm,
    fmPerGf,
    overrides: superCfg.overrides || {}
  }), [model, adjusted, superCfg, menPerFm, fmPerGf]);

  const hireScale = useMemo(() => {
    let m = 0;
    chartData.forEach((row) => { m = Math.max(m, Math.abs(row.hire || 0)); });
    const top = Math.max(1, Math.ceil(m));
    return { max: top, ticks: [-top, 0, top] };
  }, [chartData]);

  const visibleSummary = useMemo(() => {
    let peakCur = 0;
    let peakBaseline = 0;
    let peakCurWeek = null;
    let peakBaselineWeek = null;
    chartData.forEach((row) => {
      if ((row.cur || 0) >= peakCur) { peakCur = row.cur || 0; peakCurWeek = row.date; }
      if ((row.baseline || 0) >= peakBaseline) { peakBaseline = row.baseline || 0; peakBaselineWeek = row.date; }
    });
    const startIdx = cursorWeek && weeks.includes(cursorWeek)
      ? weeks.indexOf(cursorWeek)
      : (horizon === 'past' ? Math.max(0, weeks.length - 4) : 0);
    const slice = horizon === 'past'
      ? chartData.slice(Math.max(0, chartData.length - 4))
      : chartData.slice(startIdx, startIdx + 4);
    let hire4 = 0;
    let layoff4 = 0;
    slice.forEach((row) => {
      if ((row.hire || 0) > 0) hire4 += row.hire;
      else layoff4 += -(row.hire || 0);
    });
    return { peakCur, peakCurWeek, peakBaseline, peakBaselineWeek, hire4, layoff4 };
  }, [chartData, weeks, cursorWeek, horizon]);

  const plotW = Math.max(weeks.length, 1) * COL_W;
  const chartW = LABEL_W + plotW;

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveOpen(next);
      return next;
    });
  };

  const exportCsv = () => {
    const rows = [
      ['Baseline schedule', baselineLabel],
      ['FAC formula', forecastLabel, `factor ${Number(model.factor).toFixed(2)}`],
      ['Smooth (forecast)', radius === 0 ? 'off — exact P6' : `±${radius} weeks`],
      [],
      ['Section', 'Resource', ...weeks]
    ];
    SECTIONS.forEach((sec) => {
      visibleResources.forEach((r) => {
        rows.push([
          sec.title,
          `${resourceFilterLabel(r)} · ${r.label}`,
          ...weeks.map((w) => {
            const i = weekIndex.get(w);
            if (sec.id === 'hire') return (adjusted.hireBy.get(r.resourceId) || [])[i] ?? 0;
            if (sec.id === 'curMen') return (adjusted.menBy.get(r.resourceId) || [])[i] ?? 0;
            if (sec.id === 'diff') {
              return ((adjusted.menBy.get(r.resourceId) || [])[i] || 0) - (r.baselineMen[i] || 0);
            }
            return r[sec.field][i] ?? 0;
          })
        ]);
      });
      rows.push(['Total', sec.title, ...weeks.map((w) => {
        const i = weekIndex.get(w);
        if (sec.id === 'hire') return visibleResources.reduce((s, r) => s + ((adjusted.hireBy.get(r.resourceId) || [])[i] || 0), 0);
        if (sec.id === 'curMen') return visibleResources.reduce((s, r) => s + ((adjusted.menBy.get(r.resourceId) || [])[i] || 0), 0);
        if (sec.id === 'diff') {
          return visibleResources.reduce((s, r) => s + (((adjusted.menBy.get(r.resourceId) || [])[i] || 0) - (r.baselineMen[i] || 0)), 0);
        }
        return (model.totals[sec.field] || [])[i] ?? 0;
      })]);
    });
    if (progressTable?.rows?.length) {
      rows.push([]);
      progressTable.rows.forEach((row) => {
        rows.push(['Progress', `${row.label} · Cum. W Fab %`, ...weeks.map((w) => row.weeks?.[w]?.cum_w_fab ?? '')]);
        rows.push(['Progress', `${row.label} · Per. W Fab %`, ...weeks.map((w) => row.weeks?.[w]?.per_w_fab ?? '')]);
      });
    }
    downloadCsv(exportFilename({ job: jobNumber, reportSlug: 'staffing-plan', ext: 'csv' }), rows);
  };

  const sectionWeekValues = (sec, resource, week) => {
    const i = weekIndex.get(week);
    if (sec.id === 'hire') return (adjusted.hireBy.get(resource.resourceId) || [])[i] ?? 0;
    if (sec.id === 'curMen') return (adjusted.menBy.get(resource.resourceId) || [])[i] ?? 0;
    if (sec.id === 'diff') {
      return ((adjusted.menBy.get(resource.resourceId) || [])[i] || 0) - (resource.baselineMen[i] || 0);
    }
    return resource[sec.field][i] ?? 0;
  };

  const sectionTotal = (sec, week) => {
    const i = weekIndex.get(week);
    if (sec.id === 'hire') return visibleResources.reduce((s, r) => s + ((adjusted.hireBy.get(r.resourceId) || [])[i] || 0), 0);
    if (sec.id === 'curMen') return visibleResources.reduce((s, r) => s + ((adjusted.menBy.get(r.resourceId) || [])[i] || 0), 0);
    if (sec.id === 'diff') {
      return visibleResources.reduce((s, r) => s + (((adjusted.menBy.get(r.resourceId) || [])[i] || 0) - (r.baselineMen[i] || 0)), 0);
    }
    return (model.totals[sec.field] || [])[i] ?? 0;
  };

  const exportXlsx = async () => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'LGL Portal';
    const cover = wb.addWorksheet('Summary');
    cover.getColumn(1).width = 28;
    cover.getColumn(2).width = 48;
    [
      ['Job', jobNumber || ''],
      ['Baseline schedule', baselineLabel],
      ['FAC formula', `${forecastLabel} · factor ${Number(model.factor).toFixed(2)}`],
      ['Smooth (forecast)', radius === 0 ? 'off — exact P6' : `±${radius} weeks`],
      ['Data date', cursorWeek ? formatWeek(cursorWeek).tip : '—'],
      ['Peak baseline men', s.peakBaseline],
      ['Peak current men', s.peakCur],
      ['Resources', visibleResources.length],
      ['Weeks', weeks.length]
    ].forEach((row, i) => {
      cover.getCell(i + 1, 1).value = row[0];
      cover.getCell(i + 1, 2).value = row[1];
      cover.getCell(i + 1, 1).font = { bold: true };
    });

    SECTIONS.forEach((sec) => {
      const ws = wb.addWorksheet(sec.title.slice(0, 31));
      const headers = ['Resource', ...weeks.map((w) => formatWeek(w).tip)];
      ws.addRow(headers);
      visibleResources.forEach((r) => {
        ws.addRow([`${resourceFilterLabel(r)} · ${r.label}`, ...weeks.map((w) => sectionWeekValues(sec, r, w))]);
      });
      ws.addRow(['Total', ...weeks.map((w) => sectionTotal(sec, w))]);
      styleHeaderRow(ws);
      ws.getColumn(1).width = 36;
    });
    addExtrasWorksheet(wb, {
      jobNumber,
      cfg: loadExtrasCfg(jobNumber),
      projectMonths: projectMonthKeys(weeks).length,
      months: projectMonthKeys(weeks)
    });
    const buf = await wb.xlsx.writeBuffer();
    downloadBlob(
      exportFilename({ job: jobNumber, reportSlug: 'staffing-plan', ext: 'xlsx' }),
      new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    );
  };

  const exportPdf = async () => {
    const [{ default: jsPDF }, autoTableMod] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);
    const autoTable = autoTableMod.default;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const { y: letterheadY } = await applyLetterhead(doc, { instance, accounts, jobNumber, margin: 14 });
    doc.setFontSize(14);
    doc.setTextColor(15, 23, 42);
    doc.text('Staffing plan', 14, letterheadY);
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `${baselineLabel || 'Baseline'} · ${forecastLabel} · factor ${Number(model.factor).toFixed(2)}. Full week-by-week tables are in the Excel.`,
      14,
      letterheadY + 5,
      { maxWidth: 180 }
    );
    autoTable(doc, {
      startY: letterheadY + 9,
      head: [['Item', 'Value']],
      body: [
        ['Job', jobNumber || ''],
        ['Data date', cursorWeek ? formatWeek(cursorWeek).tip : '—'],
        ['Peak baseline men', String(s.peakBaseline ?? '—')],
        ['Peak current men', String(s.peakCur ?? '—')],
        ['Next 4 weeks hire', String(s.hire4 ?? 0)],
        ['Next 4 weeks layoff', String(s.layoff4 ?? 0)],
        ['Resources', String(visibleResources.length)],
        ['Weeks', String(weeks.length)]
      ],
      styles: { fontSize: 8, cellPadding: 1.6 },
      headStyles: { fillColor: [15, 118, 110], fontSize: 8 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } }
    });
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [['Resource', 'Peak current men', 'Peak baseline men']],
      body: visibleResources.map((r) => {
        const cur = Math.max(0, ...weeks.map((w) => Number(sectionWeekValues(SECTIONS.find((x) => x.id === 'curMen'), r, w)) || 0));
        const base = Math.max(0, ...weeks.map((w) => Number(sectionWeekValues(SECTIONS.find((x) => x.id === 'baselineMen'), r, w)) || 0));
        return [`${resourceFilterLabel(r)} · ${r.label}`, cur, base];
      }),
      styles: { fontSize: 7.5, cellPadding: 1.3 },
      headStyles: { fillColor: [15, 118, 110], fontSize: 8 },
      alternateRowStyles: { fillColor: [240, 253, 250] }
    });
    const extrasCfg = loadExtrasCfg(jobNumber);
    const extraMonths = projectMonthKeys(weeks);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [['Code', 'Cost item', 'Qty', 'Months']],
      body: extrasTableRows(extrasCfg, extraMonths.length).map((r) => [r[0], r[2], r[4], r[5]]),
      styles: { fontSize: 7, cellPadding: 1.2 },
      headStyles: { fillColor: [124, 58, 237], fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 243, 255] }
    });
    doc.save(exportFilename({ job: jobNumber, reportSlug: 'staffing-plan', ext: 'pdf' }));
  };

  const onExportXlsx = async () => {
    setExporting('xlsx');
    try {
      await exportXlsx();
    } catch (err) {
      console.error('Staffing xlsx export failed:', err);
      alert(`xlsx export failed: ${err.message || err}`);
    } finally {
      setExporting(null);
    }
  };
  const onExportPdf = async () => {
    setExporting('pdf');
    try {
      await exportPdf();
    } catch (err) {
      console.error('Staffing PDF export failed:', err);
      alert(`PDF export failed: ${err.message || err}`);
    } finally {
      setExporting(null);
    }
  };

  // The page-level ↓ CSV button calls whatever tab is open — register ours.
  if (csvRef) csvRef.current = exportCsv;
  if (resetRef) resetRef.current = resetAdjust;

  const hasData = weeks.length > 0 && visibleResources.length > 0;
  const s = visibleSummary;

  useEffect(() => {
    onSummaryChange?.({
      peakBaseline: s.peakBaseline,
      peakBaselineWeek: s.peakBaselineWeek ? formatWeek(s.peakBaselineWeek).tip : '—',
      peakCur: s.peakCur,
      peakCurWeek: s.peakCurWeek ? formatWeek(s.peakCurWeek).tip : '—',
      hire4: s.hire4 || 0,
      layoff4: s.layoff4 || 0,
      hireNet: (s.hire4 || 0) - (s.layoff4 || 0),
      hireHorizon: horizon === 'past' ? 'Last 4 weeks' : 'Next 4 weeks',
      weekHoursDefault,
      adjustCount,
      dataDate: cursorWeek ? formatWeek(cursorWeek).tip : '—'
    });
  }, [s, horizon, weekHoursDefault, adjustCount, cursorWeek, onSummaryChange]);

  const onChartHover = (st) => {
    const next = st?.activeLabel ? String(st.activeLabel).slice(0, 10) : null;
    if (hoverWeekRef.current === next) return;
    hoverWeekRef.current = next;
    setHoverWeek(next);
  };
  const onChartLeave = () => {
    if (hoverWeekRef.current == null) return;
    hoverWeekRef.current = null;
    setHoverWeek(null);
  };

  const emptyReason = loading
    ? 'Loading P6 curves…'
    : (!remaining?.byResource && !baseline?.byResource)
        ? 'No live P6 curves yet. Open Histogram first, or pick a Current P6 schedule in Filters.'
        : (model.resources || []).length === 0
          ? 'No hours for the current filters. Clear a discipline / shop-field / extra slicer.'
          : Array.isArray(selectedResourceIds) && selectedResourceIds.length === 0
            ? 'Every resource is cleared in Filters → Resources. Use All to bring them back.'
            : horizon === 'past'
              ? 'Nothing before the data date for these filters — no earned weeks and no baseline history. Try All or Future.'
              : 'No weeks in the selected date range.';

  const exportBtn = (label, onClick, { solid = false } = {}) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!!exporting || !hasData}
      style={{
        border: '1px solid #0f766e', background: solid ? '#0f766e' : 'white',
        color: solid ? 'white' : '#0f766e',
        borderRadius: 7, fontSize: '0.75rem', fontWeight: 800, padding: '0.35rem 0.7rem',
        cursor: exporting || !hasData ? 'wait' : 'pointer', whiteSpace: 'nowrap'
      }}
    >
      {label}
    </button>
  );

  return (
    <>
    <div style={{ minWidth: 0, maxWidth: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 8 }}>
        {exportBtn(exporting === 'xlsx' ? 'Exporting…' : '⬇ xlsx', onExportXlsx, { solid: true })}
        {exportBtn(exporting === 'pdf' ? 'Exporting…' : '⬇ PDF', onExportPdf)}
      </div>
      <div style={{
        background: 'white', border: '1px solid #e2e8f0', borderRadius: 12,
        padding: '0.5rem 0.35rem 0.5rem', minWidth: 0, maxWidth: '100%'
      }}>
        {!hasData ? (
          <div style={{ textAlign: 'center', padding: '3rem 1.5rem', color: '#64748b', maxWidth: 560, margin: '0 auto', lineHeight: 1.5 }}>
            {emptyReason}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', maxWidth: '100%', isolation: 'isolate' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', width: LABEL_W + plotW }}>
            <div style={{
              position: 'sticky', left: 0, zIndex: 8,
              width: LABEL_W, flex: 'none', background: '#ffffff',
              borderRight: '1px solid #e2e8f0',
              boxShadow: '8px 0 12px rgba(15, 23, 42, 0.12)'
            }}>
              <TimescaleStub title="Week starting · Sun" />
              <SidePanel height={MAIN_H} scaleMax={mainScale.max} ticks={mainScale.ticks} axisTitle="Men">
                  {view === 'curves' ? (
                    <>
                      <LegendRow color={CURVE_COLORS.actual} label="Actual (earned)" />
                      <LegendRow color={CURVE_COLORS.remaining} label="Remaining / forecast" />
                      <LegendRow color={CURVE_COLORS.basePast} label="Baseline to data date" />
                      <LegendRow color={CURVE_COLORS.baseFuture} label="Baseline after" />
                      <LegendRow color={CURVE_COLORS.dataDate} label="Data date" dashed />
                      {safetyOn && <LegendRow color="#dc2626" label={`Safety — ${safetyRuleLabel(safetyRule)}`} dashed />}
                    </>
                  ) : (
                    <>
                      {legendSeries.map((item) => {
                        const dim = spotlight && spotlight !== item.id;
                        return (
                          <div
                            key={item.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 5,
                              padding: '1px 0', opacity: dim ? 0.6 : 1
                            }}
                          >
                            <LegendColorSwatch
                              color={item.color}
                              pattern={item.pattern}
                              onChange={onLegendColor ? (hex) => onLegendColor(item.id, hex) : undefined}
                              onPatternChange={onLegendPattern ? (next) => onLegendPattern(item.id, next, item.color) : undefined}
                            />
                            <button
                              type="button"
                              onClick={() => setSpotlight((prev) => (prev === item.id ? null : item.id))}
                              title="Click the name to spotlight — click the square for colour or hash pattern"
                              style={{
                                display: 'block', border: 'none', background: 'transparent',
                                cursor: 'pointer', padding: 0, minWidth: 0,
                                fontSize: '0.66rem', fontWeight: 700,
                                color: dim ? '#cbd5e1' : '#334155', textAlign: 'left',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                              }}
                            >
                              {item.label}
                            </button>
                          </div>
                        );
                      })}
                      <LegendRow color="#111827" label="Baseline (dashed)" dashed />
                      {safetyOn && <LegendRow color="#dc2626" label={`Safety — ${safetyRuleLabel(safetyRule)}`} dashed />}
                      {supervisionOn && <LegendRow color="#d97706" label={`FM — 1 per ${supervisionLevels.perFm}`} dashed />}
                      {supervisionOn && <LegendRow color="#92400e" label={`GF — first at ${supervisionLevels.gfMen}`} dashed />}
                    </>
                  )}
                </SidePanel>
              <SidePanel height={HIRE_H} scaleMax={hireScale.max} scaleMin={-hireScale.max} ticks={hireScale.ticks} topMargin={4} bottomMargin={0}>
                <div style={{ fontSize: '0.66rem', fontWeight: 800, color: '#334155' }}>Hiring / layoff</div>
                <div style={{ fontSize: '0.62rem', color: '#64748b' }}>green hire · red layoff</div>
              </SidePanel>
              {supervisionOn && (
                <SupervisionStrip
                  pane="labels"
                  weeks={weeks}
                  cursorWeek={cursorWeek}
                  plan={superPlan}
                  open={superStripOpen}
                  onToggle={() => setSuperStripOpen((v) => !v)}
                />
              )}
              <Workbook
                pane="labels"
                sections={SECTIONS}
                weeks={weeks}
                weekIndex={weekIndex}
                resources={visibleResources}
                totals={model.totals}
                adjusted={adjusted}
                adjust={adjust}
                onAdjust={setHireAdjust}
                cutIndex={model.cutIndex}
                modelWeeks={model.weeks}
                hoverWeek={null}
                cursorWeek={cursorWeek}
                openSections={openSections}
                onToggleSection={toggleSection}
                tableWidth={chartW}
              />
              {progressTable?.rows?.length ? (
                <ProgressStrip
                  pane="labels"
                  weeks={weeks}
                  cursorWeek={cursorWeek}
                  tableWidth={chartW}
                  progressTable={progressTable}
                  progressScopes={progressScopes}
                  open={openSections.progress !== false}
                  onToggle={() => toggleSection('progress')}
                />
              ) : null}
            </div>
            <div style={{ width: plotW, flex: 'none' }}>
              <table style={tsTable(plotW)}>
                <colgroup>
                  {weeks.map((w) => <col key={w} style={{ width: COL_W }} />)}
                </colgroup>
                <thead>
                  <TimescaleRows weeks={weeks} cursorWeek={cursorWeek} hoverWeek={hoverWeek} />
                </thead>
              </table>
              <ChartGuard>
                <div>
                <ComposedChart
                  width={plotW}
                  height={MAIN_H}
                  data={chartData}
                  margin={{ top: M_TOP, right: 0, left: 0, bottom: M_BOT }}
                  onMouseMove={onChartHover}
                  onMouseLeave={onChartLeave}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" hide interval={0} />
                  <YAxis hide domain={[0, mainScale.max]} ticks={mainScale.ticks} />
                  <Tooltip
                    isAnimationActive={false}
                    content={(props) => <StaffingTooltip {...props} view={view} series={legendSeries} />}
                  />
                  {cursorWeek && weeks.includes(cursorWeek) && (
                    <ReferenceLine x={cursorWeek} stroke={CURVE_COLORS.dataDate} strokeWidth={2} strokeDasharray="5 3" label={{ value: 'Data date', fontSize: 10, fill: '#a16207' }} />
                  )}
                  {view === 'crew' && legendSeries.map((item) => (
                    <Bar
                      key={item.id}
                      dataKey={item.id}
                      name={item.label}
                      stackId="cur"
                      fill={item.color}
                      shape={item.pattern && item.pattern !== 'solid' ? hatchedBarShape(item.pattern) : undefined}
                      fillOpacity={spotlight ? (spotlight === item.id ? 1 : 0.15) : 1}
                      isAnimationActive={false}
                      maxBarSize={COL_W - 10}
                    >
                      {showBarCounts && (
                        <LabelList
                          dataKey={item.id}
                          position="center"
                          formatter={(v) => (v > 0 ? v : '')}
                          style={{ fontSize: 9, fill: '#ffffff', fontWeight: 800, pointerEvents: 'none' }}
                        />
                      )}
                    </Bar>
                  ))}
                  {view === 'crew' && (
                    <Line type="monotone" dataKey="baseline" name="Baseline" stroke="#111827" strokeWidth={2} strokeDasharray="6 4" dot={false} isAnimationActive={false} />
                  )}
                  {view === 'curves' && (
                    <>
                      <Line type="monotone" dataKey="basePast" name="Baseline to data date" stroke={CURVE_COLORS.basePast} strokeWidth={2.5} dot={false} connectNulls={false} isAnimationActive={false} />
                      <Line type="monotone" dataKey="baseFuture" name="Baseline after data date" stroke={CURVE_COLORS.baseFuture} strokeWidth={2.5} dot={false} connectNulls={false} isAnimationActive={false} />
                      <Line type="monotone" dataKey="actual" name="Actual (earned)" stroke={CURVE_COLORS.actual} strokeWidth={2.5} dot={false} connectNulls={false} isAnimationActive={false} />
                      <Line type="monotone" dataKey="remaining" name="Remaining / forecast" stroke={CURVE_COLORS.remaining} strokeWidth={2.5} dot={false} connectNulls={false} isAnimationActive={false} />
                    </>
                  )}
                  {showBarCounts && (
                    <Line
                      type="monotone"
                      dataKey="cur"
                      stroke="none"
                      strokeWidth={0}
                      dot={false}
                      activeDot={false}
                      legendType="none"
                      isAnimationActive={false}
                    >
                      <LabelList
                        dataKey="cur"
                        position="top"
                        offset={5}
                        formatter={(v) => (v > 0 ? v : '')}
                        style={{ fontSize: 9, fill: '#0f172a', fontWeight: 800, pointerEvents: 'none' }}
                      />
                    </Line>
                  )}
                  {safetyLevels.map((y) => (
                    <ReferenceLine
                      key={`safety-${y}`}
                      y={y}
                      stroke="#dc2626"
                      strokeOpacity={0.65}
                      strokeDasharray="7 5"
                      label={{
                        value: `${y} men → ${safetyCountWord(safetyLineLabelCount(y, safetyRule))}`,
                        position: 'insideLeft',
                        fontSize: 9,
                        fill: '#b91c1c',
                        fontWeight: 700
                      }}
                    />
                  ))}
                  {supervisionLevels.fm.filter((y) => !staffGfSet.has(y)).map((y) => (
                    <ReferenceLine
                      key={`fm-${y}`}
                      y={y}
                      stroke="#d97706"
                      strokeOpacity={0.7}
                      strokeDasharray="4 4"
                      label={{
                        value: `${y} men → ${neededAt(y, supervisionLevels.perFm)} FM`,
                        position: 'insideRight',
                        fontSize: 9,
                        fill: '#b45309',
                        fontWeight: 700
                      }}
                    />
                  ))}
                  {supervisionLevels.gf.map((y) => (
                    <ReferenceLine
                      key={`gf-${y}`}
                      y={y}
                      stroke="#92400e"
                      strokeOpacity={0.75}
                      strokeDasharray="2 3"
                      label={{
                        value: `${y} men → ${neededAt(y, supervisionLevels.perFm)} FM · ${gfNeededAt(y, supervisionLevels.gfMen)} GF`,
                        position: 'insideRight',
                        fontSize: 9,
                        fill: '#78350f',
                        fontWeight: 700
                      }}
                    />
                  ))}
                </ComposedChart>
                <ComposedChart width={plotW} height={HIRE_H} data={chartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" hide interval={0} />
                  <YAxis hide domain={[-hireScale.max, hireScale.max]} ticks={hireScale.ticks} />
                  <Tooltip isAnimationActive={false} formatter={(v) => [signed(v), 'Hiring / layoff']} labelFormatter={(d) => formatWeek(d).tip} />
                  <Bar dataKey="hire" name="Hiring / layoff" isAnimationActive={false} maxBarSize={COL_W - 14}>
                    {chartData.map((row) => (
                      <Cell key={row.date} fill={row.hire > 0 ? '#16a34a' : row.hire < 0 ? '#dc2626' : '#e2e8f0'} />
                    ))}
                  </Bar>
                  <ReferenceLine y={0} stroke="#cbd5e1" />
                </ComposedChart>
                </div>
              </ChartGuard>
              {supervisionOn && (
                <SupervisionStrip
                  pane="weeks"
                  weeks={weeks}
                  cursorWeek={cursorWeek}
                  plan={superPlan}
                  open={superStripOpen}
                  onToggle={() => setSuperStripOpen((v) => !v)}
                />
              )}
              <Workbook
                pane="weeks"
                sections={SECTIONS}
                weeks={weeks}
                weekIndex={weekIndex}
                resources={visibleResources}
                totals={model.totals}
                adjusted={adjusted}
                adjust={adjust}
                onAdjust={setHireAdjust}
                cutIndex={model.cutIndex}
                modelWeeks={model.weeks}
                hoverWeek={null}
                cursorWeek={cursorWeek}
                openSections={openSections}
                onToggleSection={toggleSection}
                tableWidth={plotW}
              />
              {progressTable?.rows?.length ? (
                <ProgressStrip
                  pane="weeks"
                  weeks={weeks}
                  cursorWeek={cursorWeek}
                  tableWidth={plotW}
                  progressTable={progressTable}
                  progressScopes={progressScopes}
                  open={openSections.progress !== false}
                  onToggle={() => toggleSection('progress')}
                />
              ) : null}
            </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

class ChartGuard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
          Chart had a display hitch.
          {' '}
          <button
            type="button"
            onClick={() => this.setState({ err: null })}
            style={{ ...ghostBtn, display: 'inline-block' }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function SupervisionStrip({ weeks, cursorWeek, plan, open, onToggle, pane = 'all' }) {
  const showLabels = pane !== 'weeks';
  const showWeeks = pane !== 'labels';
  const width = pane === 'labels' ? LABEL_W : pane === 'weeks' ? plotWidth(weeks) : (LABEL_W + plotWidth(weeks));
  const labelTd = { ...stickyCell, position: 'static', boxShadow: 'none', zIndex: 0, height: 28 };
  const lines = [
    { key: 'fm', label: 'Foremen needed', color: '#d97706', series: plan?.overall?.fm || [] },
    { key: 'gf', label: 'General foremen', color: '#6d28d9', series: plan?.overall?.gf || [] }
  ];
  const weekIdx = new Map((plan?.weeks || []).map((w, i) => [w, i]));
  return (
    <table style={{
      borderCollapse: 'separate', borderSpacing: 0, width,
      tableLayout: 'fixed', fontSize: '0.75rem', marginTop: 4
    }}>
      <colgroup>
        {showLabels && <col style={{ width: LABEL_W }} />}
        {showWeeks && weeks.map((w) => <col key={w} style={{ width: COL_W }} />)}
      </colgroup>
      <tbody>
        <tr>
          {showLabels && (
            <td style={{ ...labelTd, background: '#fffbeb', borderTop: '1px solid #fde68a', padding: 0 }}>
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                title="FM / GF needed — sum of each trade's own supervision (per P6 resource)"
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  width: '100%', border: 'none', background: 'transparent',
                  padding: '0 0.65rem', color: '#0f172a', cursor: 'pointer', textAlign: 'left',
                  height: 28, boxSizing: 'border-box'
                }}
              >
                <span style={{ color: '#b45309', fontSize: '0.7rem', width: 10 }}>{open ? '▾' : '▸'}</span>
                <span style={{ fontWeight: 800, fontSize: '0.76rem' }}>Supervision</span>
              </button>
            </td>
          )}
          {showWeeks && weeks.map((w) => (
            <td key={w} style={{ background: cursorWeek === w ? '#fef9c3' : '#fffbeb', borderTop: '1px solid #fde68a', padding: 0, height: 28 }} />
          ))}
        </tr>
        {open && lines.map((line) => (
          <tr key={line.key}>
            {showLabels && (
              <td style={{ ...labelTd, color: line.color, fontWeight: 700 }}>{line.label}</td>
            )}
            {showWeeks && weeks.map((w) => {
              const i = weekIdx.get(w);
              const v = i == null ? null : line.series[i];
              return (
                <td
                  key={w}
                  style={{
                    ...cellStyle('men', v, false, cursorWeek === w),
                    fontWeight: v ? 800 : 400,
                    color: v ? line.color : '#cbd5e1'
                  }}
                >
                  {v ? v : '·'}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProgressStrip({ weeks, cursorWeek, tableWidth, progressTable, progressScopes, open, onToggle, pane = 'all' }) {
  const scopes = (progressTable?.rows || []).filter((r) => !progressScopes?.length || progressScopes.includes(r.scope));
  const lines = scopes.flatMap((row) => ([
    { key: `${row.scope}-cum`, scope: row.scope, label: `${row.label} · cumulative`, metric: 'cum_w_fab' },
    { key: `${row.scope}-per`, scope: row.scope, label: `${row.label} · this week`, metric: 'per_w_fab' }
  ]));
  const hint = progressTable?.source === 'xer_snapshots'
    ? (progressTable.mode === 'as_of_xer'
      ? 'As-of the XER you picked — each column is that file’s earned ÷ that file’s budget'
      : 'Each yellow update Sunday is frozen to its own XER. This week = this cumulative minus last week’s')
    : 'Fallback: current schedule timespread (no weekly XER chain)';
  const showLabels = pane !== 'weeks';
  const showWeeks = pane !== 'labels';
  const width = pane === 'labels' ? LABEL_W : pane === 'weeks' ? plotWidth(weeks) : tableWidth;
  const labelTd = { ...stickyCell, position: 'static', boxShadow: 'none', zIndex: 0, height: 28 };
  return (
    <table style={{
      borderCollapse: 'separate', borderSpacing: 0, width,
      tableLayout: 'fixed', fontSize: '0.75rem', marginTop: 4
    }}>
      <colgroup>
        {showLabels && <col style={{ width: LABEL_W }} />}
        {showWeeks && weeks.map((w) => <col key={w} style={{ width: COL_W }} />)}
      </colgroup>
      <tbody>
        <tr>
          {showLabels && (
            <td style={{ ...labelTd, background: '#f1f5f9', borderTop: '1px solid #e2e8f0', padding: 0 }}>
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                title={hint}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  width: '100%', border: 'none', background: 'transparent',
                  padding: '0 0.65rem', color: '#0f172a', cursor: 'pointer', textAlign: 'left',
                  height: 28, boxSizing: 'border-box'
                }}
              >
                <span style={{ color: '#64748b', fontSize: '0.7rem', width: 10 }}>{open ? '▾' : '▸'}</span>
                <span style={{ fontWeight: 800, fontSize: '0.76rem' }}>Progress</span>
              </button>
            </td>
          )}
          {showWeeks && weeks.map((w) => (
            <td key={w} style={{ background: cursorWeek === w ? '#fef9c3' : '#f1f5f9', borderTop: '1px solid #e2e8f0', padding: 0, height: 28 }} />
          ))}
        </tr>
        {open && lines.map((line) => (
          <tr key={line.key}>
            {showLabels && <td style={labelTd}>{line.label}</td>}
            {showWeeks && weeks.map((w) => {
              const cell = progressTable.rows.find((r) => r.scope === line.scope)?.weeks?.[w];
              const v = cell?.[line.metric];
              return (
                <td
                  key={w}
                  title={cell?.xer || ''}
                  style={{
                    ...cellStyle('hours', v, false, cursorWeek === w),
                    fontWeight: v != null ? 700 : 400,
                    color: v == null ? '#cbd5e1' : '#0f172a'
                  }}
                >
                  {v == null ? '·' : `${Number(v).toFixed(1)}%`}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LegendRow({ color, label, dashed }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.66rem', fontWeight: 700, color: '#334155', padding: '1px 0' }}>
      <span style={{
        width: 14, height: 0, borderTop: `3px ${dashed ? 'dashed' : 'solid'} ${color}`, flex: 'none'
      }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
}

/**
 * Frozen left cell for a chart row: legend on the left, HTML Y-axis ticks on the right.
 * Tick geometry mirrors the chart: y(v) = top + plotH × (1 − (v − min) / (max − min)).
 */
function SidePanel({ height, scaleMax, scaleMin = 0, ticks, axisTitle, topMargin = M_TOP, bottomMargin = M_BOT, children }) {
  const plotH = height - topMargin - bottomMargin;
  const span = (scaleMax - scaleMin) || 1;
  return (
    <div style={{
      position: 'relative',
      width: LABEL_W, height, background: '#ffffff',
      boxSizing: 'border-box'
    }}>
      <div style={{ position: 'absolute', inset: 0 }}>
        {(ticks || []).map((v) => {
          const y = topMargin + plotH * (1 - (v - scaleMin) / span);
          return (
            <div key={v} style={{
              position: 'absolute', right: 4, top: y, transform: 'translateY(-50%)',
              fontSize: '0.64rem', color: '#64748b', fontVariantNumeric: 'tabular-nums'
            }}>
              {v}
            </div>
          );
        })}
        {axisTitle && (
          <div style={{
            position: 'absolute', right: 30, top: '50%',
            transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'center',
            fontSize: '0.64rem', color: '#94a3b8', letterSpacing: '0.05em'
          }}>
            {axisTitle}
          </div>
        )}
        <div style={{
          position: 'absolute', left: 6, top: topMargin, bottom: bottomMargin, width: LABEL_W - 62,
          display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', overflowX: 'hidden'
        }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function TimescaleStub({ title }) {
  return (
    <table style={tsTable(LABEL_W)}>
      <thead>
        <tr><th style={{ ...tsYear, height: TS_YEAR_H }}>&nbsp;</th></tr>
        <tr><th style={{ ...tsMonth, height: TS_MONTH_H }}>&nbsp;</th></tr>
        <tr><th style={{ ...tsDay, height: TS_DAY_H, background: '#f8fafc', color: '#64748b', fontWeight: 800 }}>{title}</th></tr>
      </thead>
    </table>
  );
}

function TimescaleRows({ weeks, cursorWeek, hoverWeek, labelCell }) {
  const { years, months } = timescaleGroups(weeks);
  return (
    <>
      <tr>
        {labelCell || null}
        {years.map((y) => (
          <th key={y.id} colSpan={y.span} style={{ ...tsYear, height: TS_YEAR_H }}>{y.label}</th>
        ))}
      </tr>
      <tr>
        {months.map((m) => (
          <th key={m.id} colSpan={m.span} style={{ ...tsMonth, height: TS_MONTH_H }}>{m.label}</th>
        ))}
      </tr>
      <tr>
        {weeks.map((w) => {
          const f = formatWeek(w);
          const now = cursorWeek === w;
          const hot = hoverWeek === w || now;
          return (
              <th
              key={w}
              title={now ? `${f.tip} · data date` : f.tip}
              style={{
                ...tsDay,
                height: TS_DAY_H,
                background: now ? '#fef9c3' : hot ? '#f0fdf4' : '#f8fafc',
                color: now ? '#a16207' : '#64748b',
                boxShadow: now ? 'inset 0 -2px 0 #eab308' : undefined
              }}
            >
              {f.day}
            </th>
          );
        })}
      </tr>
    </>
  );
}

function StatCard({ label, value, unit, sub, tone = '#0f172a' }) {
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.55rem 0.7rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.03em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: tone, lineHeight: 1.2 }}>
        {value} <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8' }}>{unit}</span>
      </div>
      <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function StaffingTooltip({ active, payload, label, view, series }) {
  if (!active || !payload?.length) return null;
  const byKey = new Map((payload || []).filter((p) => p && p.dataKey != null).map((p) => [p.dataKey, p]));
  const row = (name, color, value) => (
    <div key={name} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
      <span style={{ color }}>{name}</span>
      <span>{value} men</span>
    </div>
  );
  const names = {
    actual: ['Actual (earned)', CURVE_COLORS.actual],
    remaining: ['Remaining / forecast', CURVE_COLORS.remaining],
    basePast: ['Baseline', CURVE_COLORS.basePast],
    baseFuture: ['Baseline', CURVE_COLORS.baseFuture]
  };
  return (
    <div style={{
      background: 'white', border: '1px solid #e2e8f0', borderRadius: 8,
      padding: '0.5rem 0.65rem', boxShadow: '0 8px 24px rgba(15,23,42,0.12)', fontSize: '0.75rem'
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{formatWeek(label).tip}</div>
      {view === 'crew' && (series || []).map((sItem) => {
        const p = byKey.get(sItem.id);
        if (!p || !p.value) return null;
        return row(sItem.label, sItem.color, p.value);
      })}
      {view === 'crew' && byKey.get('baseline')?.value != null && (
        <div style={{ marginTop: 4, borderTop: '1px solid #e2e8f0', paddingTop: 4 }}>
          {row('Baseline', '#111827', byKey.get('baseline').value)}
        </div>
      )}
      {view === 'curves' && ['actual', 'remaining', 'basePast', 'baseFuture'].map((k) => {
        const p = byKey.get(k);
        if (!p || p.value == null) return null;
        return row(names[k][0], names[k][1], p.value);
      })}
    </div>
  );
}

function HireCell({ base, override, hot, isCursor, locked, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const effective = override == null ? base : override;
  const adjusted = override != null;

  if (editing) {
    const commit = () => {
      setEditing(false);
      const trimmed = String(val).trim();
      if (trimmed === '') onCommit(null);
      else onCommit(Math.round(Number(trimmed) || 0));
    };
    return (
      <td style={{ padding: 0, borderBottom: '1px solid #f1f5f9', width: COL_W, height: 28, boxSizing: 'border-box' }}>
        <input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          style={{ width: COL_W - 4, boxSizing: 'border-box', fontSize: '0.72rem', textAlign: 'center', border: '1px solid #0f766e', borderRadius: 3 }}
        />
      </td>
    );
  }

  const style = cellStyle('delta', effective, hot, isCursor);
  if (adjusted) {
    style.background = hot ? '#fef3c7' : '#fffbeb';
    style.fontWeight = 800;
    style.textDecoration = 'underline dotted';
  }
  return (
    <td
      onDoubleClick={() => {
        if (locked) return;
        setVal(effective ? String(effective) : '');
        setEditing(true);
      }}
      title={locked
        ? 'Past week — history is not editable'
        : adjusted
          ? `Override (default ${signed(base)}) — double-click to edit, empty resets`
          : 'Double-click to override'}
      style={{ ...style, cursor: locked ? 'default' : 'cell' }}
    >
      {formatCell('delta', effective)}
    </td>
  );
}

function Workbook({
  sections, weeks, weekIndex, resources, totals, adjusted, adjust, onAdjust,
  cutIndex, modelWeeks, hoverWeek, cursorWeek, openSections, onToggleSection, tableWidth,
  pane = 'all'
}) {
  const cellValue = (sec, r, i) => {
    if (sec.id === 'hire') return (adjusted.hireBy.get(r.resourceId) || [])[i] || 0;
    if (sec.id === 'curMen') return (adjusted.menBy.get(r.resourceId) || [])[i] || 0;
    if (sec.id === 'diff') return ((adjusted.menBy.get(r.resourceId) || [])[i] || 0) - (r.baselineMen[i] || 0);
    return r[sec.field][i] || 0;
  };
  const totalValue = (sec, i) => {
    if (sec.id === 'hire') return resources.reduce((s, r) => s + ((adjusted.hireBy.get(r.resourceId) || [])[i] || 0), 0);
    if (sec.id === 'curMen') return resources.reduce((s, r) => s + ((adjusted.menBy.get(r.resourceId) || [])[i] || 0), 0);
    if (sec.id === 'diff') return resources.reduce((s, r) => s + (((adjusted.menBy.get(r.resourceId) || [])[i] || 0) - (r.baselineMen[i] || 0)), 0);
    return (totals[sec.field] || [])[i] || 0;
  };

  const showLabels = pane !== 'weeks';
  const showWeeks = pane !== 'labels';
  const width = pane === 'labels' ? LABEL_W : pane === 'weeks' ? plotWidth(weeks) : tableWidth;
  const labelTd = {
    ...stickyCell,
    position: 'static',
    boxShadow: 'none',
    zIndex: 0,
    height: 28
  };

  return (
    <table style={{
      borderCollapse: 'separate', borderSpacing: 0, width,
      tableLayout: 'fixed', fontSize: '0.75rem'
    }}>
      <colgroup>
        {showLabels && <col style={{ width: LABEL_W }} />}
        {showWeeks && weeks.map((w) => <col key={w} style={{ width: COL_W }} />)}
      </colgroup>
      <thead>
        {showLabels && !showWeeks ? (
          <>
            <tr><th style={{ ...tsYear, height: TS_YEAR_H }}>&nbsp;</th></tr>
            <tr><th style={{ ...tsMonth, height: TS_MONTH_H }}>&nbsp;</th></tr>
            <tr><th style={{ ...tsDay, height: TS_DAY_H, textAlign: 'left', paddingLeft: 12 }}>Resource</th></tr>
          </>
        ) : (
          <TimescaleRows
            weeks={weeks}
            cursorWeek={cursorWeek}
            hoverWeek={hoverWeek}
            labelCell={showLabels ? (
              <th rowSpan={3} style={{ ...stickyLabelHead, position: 'static', boxShadow: 'none', textAlign: 'left', paddingLeft: 12 }}>Resource</th>
            ) : null}
          />
        )}
      </thead>
      <tbody>
        {sections.map((sec) => {
          const open = openSections?.[sec.id] !== false;
          return (
            <React.Fragment key={sec.id}>
              <tr>
                {showLabels && (
                  <td style={{ ...labelTd, background: '#f1f5f9', borderTop: '1px solid #e2e8f0', padding: 0 }}>
                    <button
                      type="button"
                      onClick={() => onToggleSection(sec.id)}
                      aria-expanded={open}
                      title={sec.hint}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        width: '100%', border: 'none', background: 'transparent',
                        padding: '0 0.65rem', color: '#0f172a', cursor: 'pointer', textAlign: 'left',
                        height: 28, boxSizing: 'border-box'
                      }}
                    >
                      <span style={{ color: '#64748b', fontSize: '0.7rem', width: 10 }}>{open ? '▾' : '▸'}</span>
                      <span style={{ fontWeight: 800, fontSize: '0.76rem' }}>{sec.title}</span>
                    </button>
                  </td>
                )}
                {showWeeks && weeks.map((w) => (
                  <td key={w} style={{ background: cursorWeek === w ? '#fef9c3' : '#f1f5f9', borderTop: '1px solid #e2e8f0', padding: 0, height: 28 }} />
                ))}
              </tr>
              {open && resources.map((r) => (
                <tr key={`${sec.id}-${r.resourceId}`}>
                  {showLabels && (
                    <td style={labelTd}>
                      <span style={{
                        display: 'inline-block', width: 8, height: 8, borderRadius: 99,
                        background: resourceColor(r), marginRight: 6
                      }} />
                      <span title={`${r.resourceId}${r.weekHrCnt ? ` · ${r.weekHrCnt} h/wk calendar` : ''}`}>
                        {resourceFilterLabel(r)}
                      </span>
                    </td>
                  )}
                  {showWeeks && weeks.map((w) => {
                    const i = weekIndex.get(w);
                    const hot = hoverWeek === w;
                    const isCursor = cursorWeek === w;
                    if (sec.editable) {
                      const override = adjust[`${r.resourceId}|${w}`];
                      return (
                        <HireCell
                          key={w}
                          base={(r.curMen[i] || 0) - (i > 0 ? (r.curMen[i - 1] || 0) : 0)}
                          override={override == null ? null : override}
                          hot={hot}
                          isCursor={isCursor}
                          locked={i < cutIndex}
                          onCommit={(v) => onAdjust(r.resourceId, modelWeeks[i], v)}
                        />
                      );
                    }
                    const v = cellValue(sec, r, i);
                    return (
                      <td key={w} style={cellStyle(sec.kind, v, hot, isCursor)}>
                        {formatCell(sec.kind, v)}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {open && (
                <tr>
                  {showLabels && <td style={{ ...labelTd, fontWeight: 800 }}>Total</td>}
                  {showWeeks && weeks.map((w) => {
                    const i = weekIndex.get(w);
                    const v = totalValue(sec, i);
                    return (
                      <td key={w} style={{ ...cellStyle(sec.kind, v, hoverWeek === w, cursorWeek === w), fontWeight: 800 }}>
                        {formatCell(sec.kind, v)}
                      </td>
                    );
                  })}
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function plotWidth(weeks) {
  return Math.max((weeks || []).length, 1) * COL_W;
}

function formatCell(kind, v) {
  if (!v) return '·';
  if (kind === 'delta') return signed(Math.round(v));
  if (kind === 'hours') return String(Math.round(v));
  return String(v);
}

function cellStyle(kind, v, hot, isCursor) {
  let color = kind === 'hours' ? '#475569' : '#334155';
  let bg = isCursor ? '#fefce8' : hot ? '#f0fdf4' : 'white';
  if (kind === 'delta') {
    if (v > 0) { color = '#15803d'; bg = hot ? '#dcfce7' : isCursor ? '#fefce8' : '#f0fdf4'; }
    else if (v < 0) { color = '#b91c1c'; bg = hot ? '#fee2e2' : isCursor ? '#fefce8' : '#fef2f2'; }
    else { color = '#cbd5e1'; }
  }
  return {
    textAlign: 'center',
    padding: '0.28rem 0.1rem',
    borderBottom: '1px solid #f1f5f9',
    color,
    background: bg,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    width: COL_W,
    overflow: 'hidden',
    fontSize: kind === 'hours' ? '0.68rem' : undefined,
    height: 28,
    boxSizing: 'border-box'
  };
}

const tsTable = (w) => ({
  borderCollapse: 'separate',
  borderSpacing: 0,
  width: w,
  tableLayout: 'fixed',
  fontSize: '0.72rem'
});

const tsYear = {
  background: '#eef2f7',
  borderBottom: '1px solid #e2e8f0',
  borderLeft: '1px solid #e2e8f0',
  color: '#334155',
  fontWeight: 800,
  padding: '0.18rem 0',
  textAlign: 'center',
  boxSizing: 'border-box'
};

const tsMonth = {
  background: '#f4f7fb',
  borderBottom: '1px solid #e2e8f0',
  borderLeft: '1px solid #e2e8f0',
  color: '#475569',
  fontWeight: 700,
  fontSize: '0.66rem',
  padding: '0.14rem 0',
  textAlign: 'center',
  boxSizing: 'border-box'
};

const tsDay = {
  borderBottom: '1px solid #e2e8f0',
  fontWeight: 800,
  fontSize: '0.7rem',
  padding: '0.2rem 0',
  textAlign: 'center',
  boxSizing: 'border-box'
};

const stickyLabelHead = {
  position: 'sticky',
  left: 0,
  zIndex: 8,
  background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  boxShadow: '8px 0 0 #f8fafc, 10px 0 10px rgba(15, 23, 42, 0.08)',
  padding: '0.3rem 0.4rem',
  textAlign: 'center',
  fontWeight: 700,
  color: '#334155',
  verticalAlign: 'bottom'
};

const stickyCell = {
  position: 'sticky',
  left: 0,
  background: '#ffffff',
  zIndex: 4,
  padding: '0.28rem 0.75rem',
  borderBottom: '1px solid #f1f5f9',
  boxShadow: '8px 0 0 #ffffff, 10px 0 10px rgba(15, 23, 42, 0.08)',
  fontWeight: 600,
  color: '#0f172a',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

const ghostBtn = {
  border: '1px solid #cbd5e1',
  background: '#f8fafc',
  borderRadius: 7,
  fontSize: '0.75rem',
  fontWeight: 600,
  padding: '0.28rem 0.65rem',
  cursor: 'pointer',
  color: '#334155'
};

export default StaffingPlanTab;
